import './Row.scss';
import { Container } from '@components/Component/Container/Container.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
/**
 * `Row` represents a full screen width container on a form that can contain components or grids. Dont mix grids with other components in the same row.
 * It extends the `Container` class.
 */
export class Row extends Container {
    get gutterX_xs() { return this._gutterX_xs; }
    set gutterX_xs(value) {
        this.toggleGutterXClass(this._gutterX_xs, value, '');
        this._propertySet('gutterX_xs', value);
    }
    get gutterX_sm() { return this._gutterX_sm; }
    set gutterX_sm(value) {
        this.toggleGutterXClass(this._gutterX_sm, value, 'sm');
        this._propertySet('gutterX_sm', value);
    }
    get gutterX_md() { return this._gutterX_md; }
    set gutterX_md(value) {
        this.toggleGutterXClass(this._gutterX_md, value, 'md');
        this._propertySet('gutterX_md', value);
    }
    get gutterX_lg() { return this._gutterX_lg; }
    set gutterX_lg(value) {
        this.toggleGutterXClass(this._gutterX_lg, value, 'lg');
        this._propertySet('gutterX_lg', value);
    }
    get gutterX_xl() { return this._gutterX_xl; }
    set gutterX_xl(value) {
        this.toggleGutterXClass(this._gutterX_xl, value, 'xl');
        this._propertySet('gutterX_xl', value);
    }
    get gutterY_xs() { return this._gutterY_xs; }
    set gutterY_xs(value) {
        this.toggleGutterYClass(this._gutterY_xs, value, '');
        this._propertySet('gutterY_xs', value);
    }
    get gutterY_sm() { return this._gutterY_sm; }
    set gutterY_sm(value) {
        this.toggleGutterYClass(this._gutterY_sm, value, 'sm');
        this._propertySet('gutterY_sm', value);
    }
    get gutterY_md() { return this._gutterY_md; }
    set gutterY_md(value) {
        this.toggleGutterYClass(this._gutterY_md, value, 'md');
        this._propertySet('gutterY_md', value);
    }
    get gutterY_lg() { return this._gutterY_lg; }
    set gutterY_lg(value) {
        this.toggleGutterYClass(this._gutterY_lg, value, 'lg');
        this._propertySet('gutterY_lg', value);
    }
    get gutterY_xl() { return this._gutterY_xl; }
    set gutterY_xl(value) {
        this.toggleGutterYClass(this._gutterY_xl, value, 'xl');
        this._propertySet('gutterY_xl', value);
    }
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'Row';
        this._gutterX_xs = 'normal';
        this._gutterY_xs = 'none';
        this.designerDragType = 'row';
    }
    draw() {
        super.draw();
        this.rootElement.classList.add('row');
        // orders should be applied prior to draw so no need for observing those properties. 
        this.updateLayout();
        if (this.formDesignerMode) {
            this.setupForFormDesigner();
        }
    }
    toggleGutterXClass(oldGutterX, newGutterX, breakpoint = '') {
        if (!this.drawn) {
            return;
        }
        if (oldGutterX === 'normal') {
            this.rootElement.classList.remove(this.gutter(breakpoint, oldGutterX, 'x'));
        }
        if (newGutterX === 'normal') {
            this.rootElement.classList.add(this.gutter(breakpoint, newGutterX, 'x'));
        }
    }
    toggleGutterYClass(oldGutterY, newGutterY, breakpoint = '') {
        if (!this.drawn) {
            return;
        }
        if (oldGutterY === 'normal') {
            this.rootElement.classList.remove(this.gutter(breakpoint, oldGutterY, 'y'));
        }
        if (newGutterY === 'normal') {
            this.rootElement.classList.add(this.gutter(breakpoint, newGutterY, 'y'));
        }
    }
    gutter(prefix, gutter = 'none', xy) {
        if (gutter !== 'normal') {
            return '';
        }
        if (prefix) {
            return `g${xy}-${prefix}`;
        }
        return `g${xy}`;
    }
    setupForFormDesigner() {
        const addComponentButton = DIContainer.getInstance('IconManager').getIconElement('add');
        addComponentButton.classList.add('form-designer-add');
        this.rootElement.appendChild(addComponentButton);
        this.rootElement.classList.add('drop-target-row');
    }
    /**
     * Re-applies QStrap classes to row.
     * Call this if you make changes to order after drawing.
     *
     * @example
     * const row = form.componentGet('row1')
     * row.order_sm = 6;
     * row.updateLayout();
     */
    updateLayout() {
        // Classes to be added
        const newClassList = [];
        newClassList.push(this.ord('', this.order));
        newClassList.push(this.ord('sm', this.order_sm));
        newClassList.push(this.ord('md', this.order_md));
        newClassList.push(this.ord('lg', this.order_lg));
        newClassList.push(this.ord('xl', this.order_xl));
        newClassList.push(this.gutter('', this.gutterY_xs, 'y'));
        newClassList.push(this.gutter('sm', this.gutterY_sm, 'y'));
        newClassList.push(this.gutter('md', this.gutterY_md, 'y'));
        newClassList.push(this.gutter('lg', this.gutterY_lg, 'y'));
        newClassList.push(this.gutter('xl', this.gutterY_xl, 'y'));
        newClassList.push(this.gutter('', this.gutterX_xs, 'x'));
        newClassList.push(this.gutter('sm', this.gutterX_sm, 'x'));
        newClassList.push(this.gutter('md', this.gutterX_md, 'x'));
        newClassList.push(this.gutter('lg', this.gutterX_lg, 'x'));
        newClassList.push(this.gutter('xl', this.gutterX_xl, 'x'));
        // Get current classes and filter out col-* and order-*
        const currentClasses = this.rootElement.className.split(' ').filter(cls => !cls.startsWith('order-') && !cls.startsWith('gx') && !cls.startsWith('gy'));
        // Combine new classes with retained classes
        this.rootElement.className = [...currentClasses, ...newClassList].join(' ');
    }
    // TODO: move this to a QStrap.ts utility
    /**
     * Utility function for assigning order- classes
     * @param prefix - the screen breakpoint size
     * @param order - a number
     * @private
     */
    ord(prefix, order) {
        if (!order) {
            return '';
        }
        if (prefix) {
            return `order-${prefix}-${order}`;
        }
        return `order-${order}`;
    }
    formDesignerPropertiesGet(properties) {
        properties = super.formDesignerPropertiesGet(properties);
        // other breakpoints are implemented if we need to add them as properties we can
        properties.gutterX_xs = ({
            id: 'gutterX_xs',
            value: this.gutterX_xs,
            componentType: 'InputCheckbox',
            componentProperties: {
                checkedValue: 'normal',
                unCheckedValue: 'none'
            },
            captionKey: 'formbuilder_row_gutterx_xs',
            propertyGroup: 'layout',
            propertyIndex: 1,
            saveFlag: () => this.gutterX_xs !== 'normal'
        });
        properties.gutterY_xs = ({
            id: 'gutterY_xs',
            value: this.gutterY_xs,
            componentType: 'InputCheckbox',
            componentProperties: {
                checkedValue: 'normal',
                unCheckedValue: 'none'
            },
            captionKey: 'formbuilder_row_guttery_xs',
            propertyGroup: 'layout',
            propertyIndex: 2,
            saveFlag: () => this.gutterY_xs === 'normal'
        });
        return properties;
    }
}
Row.VERSION = '$Revision: 111642 $';
